import { Fachbaustein, FachschluesselTyp, Feature, MasterProduct } from '@ps20/bff-models';
import {
  convertToZusatzFeature,
  Eigenschaft,
  Gefahr,
  IncludedLeistung,
  Leistung,
  Produkt,
  Produktlinie,
  ZusatzoptionItem,
} from '@ps20-frontend/frontend-models';
import { selectFeatureToggle, selectPreisverzeichnisTestValue } from '@ps20-frontend/selector';
import { IAppState } from '../../localStore';

export function buildProdukt(masterprodukt: MasterProduct, state: IAppState): Produkt {
  return {
    produktlinien: buildProductLinien(masterprodukt, state),
    uebergreifend: masterprodukt.uebergreifendeLeistungen || [],
    hspLfnr: masterprodukt.hspLfnr,
    spartenType: masterprodukt.sparte,
  };
}

function buildProductLinien(masterprodukt: MasterProduct, state: IAppState): Array<Produktlinie> {
  return masterprodukt.salesProdukte.map(salesProdukt => {
    let preisverzeichnis = salesProdukt.preisverzeichnis;
    if (selectFeatureToggle(state, Feature.PVZ_ERSATZGESCHAEFT_MOCK)) {
      preisverzeichnis = selectPreisverzeichnisTestValue(state);
    }
    return {
      produktName: salesProdukt.produktline,
      leistungen: buildLeistungen(salesProdukt.leistungen, masterprodukt.allgemeineFachschluessel),
      title: salesProdukt.name,
      kurztext: salesProdukt.kurztext,
      langtext: salesProdukt.langtext,
      tarifBezeichnung: salesProdukt.tarifBezeichnung,
      selected: salesProdukt.initialSelektiert,
      rbdMax: salesProdukt.rbdMax,
      rbdAktuell: salesProdukt.rbdAktuell,
      preisverzeichnis,
      segmentierungskennzeichen: salesProdukt.segmentierungskennzeichen,
      gefahrenPrefix: salesProdukt.gefahrenPrefix,
      deactivated: false,
      zusatzoptionen: buildZusatzoptionen(salesProdukt.zusatzoptionen),
      gefahren: buildGefahren(salesProdukt.gefahren),
    };
  });
}

export function buildGefahren(gefahren: Fachbaustein[]): Array<Gefahr> {
  return gefahren?.map(gefahr => {
    const {
      leistung,
      name,
      fachschluessel,
      reihenfolge,
      writeToEinreichenXML,
      textId,
      fachschluesselTyp,
    } = gefahr;
    return {
      id: textId,
      label: leistung,
      name,
      fachschluessel,
      reihenfolge,
      writeToEinreichenXML,
      isSelectable: fachschluesselTyp === FachschluesselTyp.OPTIONAL,

      isSelected: true,
      bruttopreis: 0,
    };
  });
}

function buildLeistungen(
  leistungen: Fachbaustein[],
  allgemeineLeistungen: Fachbaustein[],
): Array<Leistung> {
  const leistungenArr: Array<Leistung> = leistungen.map(leistung => ({
    id: leistung.textId,
    name: leistung.name,
    fachschluessel: leistung.fachschluessel,
    fachschluesselTyp: leistung.fachschluesselTyp,
    eigenschaft: (leistung.eigenschaftType || leistung.eigenschaft) as Eigenschaft, // cast mit aktuellerer Ts-Version nicht nötig
    summenWerte: leistung.summenWerte,
    eigenschaftInfo: leistung.eigenschaftInfo,
    info: leistung.info,
    reihenfolge: leistung.reihenfolge,
    writeToEinreichenXML: leistung.writeToEinreichenXML,
    summenBezeichnung: leistung.summenBezeichnung,
    summe: leistung.summe,
    zusatzFeature: leistung.zusatzFeature ? convertToZusatzFeature(leistung.zusatzFeature) : null,
    isWeitereLeistung: leistung.weitereLeistung,
    hidden: leistung.hidden,
    label: leistung.leistung,
    kategorie: leistung.kategorie,
    kategorieId: leistung.kategorieId,
    gefahrId: leistung.gefahrId,
    icon: leistung.icon,
  }));

  const allgemeineLeistungenArr = allgemeineLeistungen.map(allgemeineLeistung => ({
    id: allgemeineLeistung.textId,
    name: allgemeineLeistung.name,
    label: allgemeineLeistung.leistung,
    fachschluessel: allgemeineLeistung.fachschluessel,
    fachschluesselTyp: allgemeineLeistung.fachschluesselTyp,
    eigenschaft: allgemeineLeistung.eigenschaft,
    summenWerte: allgemeineLeistung.summenWerte,
    hidden: true,
    isWeitereLeistung: false,
    reihenfolge: allgemeineLeistung.reihenfolge,
    writeToEinreichenXML: allgemeineLeistung.writeToEinreichenXML,
    zusatzFeature: allgemeineLeistung.zusatzFeature
      ? convertToZusatzFeature(allgemeineLeistung.zusatzFeature)
      : null,
  }));

  return leistungenArr.concat(allgemeineLeistungenArr);
}

function buildIncludedLeistungen(includedLeistungen: Fachbaustein[]): Array<IncludedLeistung> {
  return includedLeistungen?.map(includedLeistungBff => {
    return {
      fachschluessel: includedLeistungBff.fachschluessel,
      textId: includedLeistungBff.textId,
      leistung: includedLeistungBff.leistung,
      reihenfolge: includedLeistungBff.reihenfolge,
    };
  });
}

function buildZusatzoptionen(salesProduktZusatzoptionen: Fachbaustein[]): Array<ZusatzoptionItem> {
  const zusatzoptionenItems: Array<ZusatzoptionItem> = salesProduktZusatzoptionen.map(
    zusatzoption => {
      const { fachschluessel, reihenfolge, writeToEinreichenXML, eigenschaft, summenWerte } =
        zusatzoption;
      const isInclusive = zusatzoption.fachschluesselTyp === FachschluesselTyp.REQUIRED;
      const includedLeistungen = buildIncludedLeistungen(zusatzoption.includedLeistungen);
      return {
        id: zusatzoption.textId,
        name: zusatzoption.name,
        info: zusatzoption.info,
        bruttopreis: 0,
        nettopreisProJahr: 0,
        isSelected: isInclusive,
        isInclusive,
        fachschluessel,
        reihenfolge,
        writeToEinreichenXML,
        includedLeistungen,
        eigenschaft,
        summenWerte,
        leistung: zusatzoption.leistung,
        initialSelektiert: zusatzoption.initialSelektiert,
        icon: zusatzoption.icon,
        title: zusatzoption.title,
        beschreibung: zusatzoption.beschreibung,
        anzahlWohnungen: zusatzoption.anzahlWohnungen,
        label: zusatzoption.label,
        titleprint: zusatzoption.titlePrint,
        tkvRadio: {
          title: zusatzoption?.radio?.title,
          bis_2000_100: {
            label: zusatzoption?.radio?.bis_2000_100?.label,
            disabledInfo: zusatzoption?.radio?.bis_2000_100?.disabledInfo,
            disabledAria: zusatzoption?.radio?.bis_2000_100?.disabledAria,
          },
          bis_5000_100: {
            label: zusatzoption?.radio?.bis_5000_100?.label,
            disabledInfo: zusatzoption?.radio?.bis_5000_100?.disabledInfo,
            disabledAria: zusatzoption?.radio?.bis_5000_100?.disabledAria,
          },
          bis_2000_50: {
            label: zusatzoption?.radio?.bis_2000_50?.label,
            disabledInfo: zusatzoption?.radio?.bis_2000_50?.disabledInfo,
            disabledAria: zusatzoption?.radio?.bis_2000_50?.disabledAria,
          },
        },
        besonderheiten: {
          link: zusatzoption?.besonderheiten?.link,
          closeButton: zusatzoption?.besonderheiten?.closeButton,
        },
        schadenbeispiele: {
          link: zusatzoption?.schadenbeispiele?.link,
          title: zusatzoption?.schadenbeispiele?.title,
          reference: zusatzoption?.schadenbeispiele?.reference,
          right: {
            header: zusatzoption?.schadenbeispiele?.right?.headline,
            subheadline: zusatzoption?.schadenbeispiele?.right?.subheadline,
          },
          left: {
            header: zusatzoption?.schadenbeispiele?.left?.headline,
            subheadline: zusatzoption?.schadenbeispiele?.left?.subheadline,
          },
        },
        disabledLabel: zusatzoption.disabled,
        extremwetterschutz: {
          text: zusatzoption?.extremwetterschutz?.text,
          info: zusatzoption?.extremwetterschutz?.info,
          durchschnittlich: zusatzoption?.extremwetterschutz?.durchschnittlich,
          erhoeht: zusatzoption?.extremwetterschutz?.erhoeht,
          gravierend: zusatzoption?.extremwetterschutz?.gravierend,
        },
      };
    },
  );
  return zusatzoptionenItems.sort((a, b) => a.reihenfolge - b.reihenfolge);
}
